import { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useLocation } from "react-router-dom";
import useAuth from "../hooks/useAuth";
import { usePermissions } from "../utils/permissions";
import { Cap, Level } from "../utils/rbacCatalog";
import { useTranslation } from "react-i18next";
import { AiOutlineArrowLeft } from "react-icons/ai";
import UsersListComponent from "../components/pages/dashboard/users/UsersListComponent";
import { USER_LIST_COMPONENT_TYPE } from "../constants/types";
import { useGetSearchUsers } from "../hooks/users";
import { BiPlus } from "react-icons/bi";
import AddToGroupModal from "../components/pages/dashboard/users/AddToGroupModal";
import { CustomButton } from "../components/atoms/CustomButton";
import Pagination from "../components/public/Pagination";
import SecureStorage from "../utils/secureStorage";

interface LocationState {
  query?: string;
}

const SearchPage = () => {
  const { user, customer_identification } = useAuth();
  const permissions = usePermissions(user);
  const canAddToGroup = permissions.can(Cap.USERS_UPDATE, Level.WRITE);
  const [t] = useTranslation();
  const location = useLocation() as { state?: LocationState };

  const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(new Set());
  const [limit] = useState<10 | 30 | 50>(
    SecureStorage.getUsersListLimit() as 10 | 30 | 50
  );
  const [offset, setOffset] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState(location.state?.query || "");

  useEffect(() => {
    if (location.state?.query) {
      setSearchQuery(location.state.query);
      setOffset(0);
    }
  }, [location.state?.query]);

  const queryData = {
    customer_identification: customer_identification,
    entityIdentification: user.entity_identification,
  };
  const {
    isLoading,
    isError,
    isSuccess,
    data,
    refetch: refetchUsers,
  } = useGetSearchUsers({
    ...queryData,
    query: searchQuery,
    limit: limit,
    pageParam: offset,
  });
  const tooManyResults = !Array.isArray(data) && data?.tooManyResults === true;
  const users = Array.isArray(data) ? data : data?.users ?? [];
  const pagination = Array.isArray(data) ? null : data?.pagination ?? null;

  const calculateCurrentPage = useCallback(() => {
    return offset;
  }, [offset]);

  const handlePageChange = (pageNumber: number) => {
    setOffset(pageNumber);
  };

  let content;

  if (isLoading) content = <div className="p-3 text-center">Loading...</div>;

  if (isError)
    content = <CustomButton onClick={() => refetchUsers()} title="Refetch" />;

  if (isSuccess && tooManyResults)
    content = (
      <div className="p-6 text-center">
        <h4 className="text-gray-800 font-semibold mb-2">
          {t("dashboard.searchPage.tooMany.title")}
        </h4>
        <p className="text-gray-600 text-sm">
          {t("dashboard.searchPage.tooMany.description")}
        </p>
      </div>
    );

  if (isSuccess && !tooManyResults)
    content =
      users.length > 0 ? (
        <>
          <div className="overflow-x-auto">
            <UsersListComponent
              users={users}
              isError={isError}
              isLoading={isLoading}
              isSuccess={isSuccess}
              refetch={refetchUsers}
              selectedRowIds={selectedRowIds}
              setSelectedRowIds={setSelectedRowIds}
              type={USER_LIST_COMPONENT_TYPE.USERS}
              offset={offset}
              setOffset={setOffset}
              limit={users?.length}
            />
          </div>
          {pagination && pagination.total > 0 && (
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 my-6 px-2">
              <div className="text-sm text-gray-600 text-center sm:text-left">
                <span className="font-normal">
                  {t("dashboard.usersList.key2")}{" "}
                </span>
                <span className="font-semibold">{offset * limit + 1}</span>
                <span className="font-normal">
                  {" "}
                  {t("dashboard.usersList.key3")}{" "}
                </span>
                <span className="font-semibold">
                  {users?.length ? users.length + offset * limit : 0}
                </span>
                <span className="font-normal">
                  {" "}
                  {t("dashboard.usersList.key4")}{" "}
                </span>
                <span className="font-semibold">{pagination?.total ?? 0}</span>
                <span className="font-normal">
                  {" "}
                  {t("dashboard.usersList.key5")}
                </span>
              </div>

              <Pagination
                pageCount={pagination?.total_pages}
                currentPage={calculateCurrentPage()}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </>
      ) : (
        <div className="p-3 text-center">
          <h4>{t("dashboard.searchPage.text-02")}</h4>
          <p>
            {t("dashboard.searchPage.text-03")}{" "}
            {location.state?.query ? (
              <b>{location.state?.query}</b>
            ) : (
              "this search key"
            )}
          </p>
        </div>
      );

  return (
    <div className="users-page">
      <Helmet>
        <title>Search users | Super User Manager</title>
        <meta name="description" content="Search users" />
        <link
          rel="canonical"
          href={`${process.env.REACT_APP_HOST_URL}/control-panel/search`}
        />
      </Helmet>

      <div className="container mx-auto py-6">
        {location.state?.query ? (
          <div className="flex items-center gap-3 mb-6 justify-between">
            <div className="flex items-center gap-3 ">
              <Link
                to={"/control-panel/users"}
                className="btn btn-link text-decoration-none text-dark d-flex align-items-center me-3 px-0"
              >
                <AiOutlineArrowLeft />
              </Link>
              <h4 className="text-gray-700 text-xl font-bold font-figtree leading-loose">
                {t("dashboard.searchPage.text-04")}
                <b className="ms-2"> {location.state?.query}</b>
              </h4>
            </div>

            {selectedRowIds?.size > 1 && canAddToGroup && (
              <div
                role="button"
                onClick={() => setShowModal(true)}
                className="px-2 py-3 bg-white rounded-md shadow-sm border border-gray-300 justify-center items-center gap-1 inline-flex overflow-hidden"
              >
                <div data-svg-wrapper className="relative">
                  <BiPlus className="text-gray-500" size={18} />
                </div>
                <div className="text-gray-700 text-sm font-medium font-figtree leading-tight">
                  {t("dashboard.entities.viewEntityUser.key14")}
                </div>
              </div>
            )}
          </div>
        ) : (
          <p className="p-3">{t("dashboard.searchPage.text-05")}</p>
        )}
        {content}
      </div>

      <AddToGroupModal
        userList={selectedRowIds}
        show={showModal}
        onHide={() => setShowModal(false)}
        onResolved={() => setSelectedRowIds(new Set())}
      />
    </div>
  );
};

export default SearchPage;
